import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { EyeOff, Lock } from 'lucide-react';
import {
  COUNTRY_SUGGESTIONS,
  RUM_TYPE_LABELS,
  RUM_TYPES,
  upsertGuessSchema,
  type RumType,
} from '@cercle/shared';
import { useApi, useApp } from '@/shared/app-context';
import { useAsync } from '@/shared/hooks/useAsync';
import { useSessionSocket } from '@/shared/ws/useSessionSocket';
import {
  Button,
  EmptyState,
  ErrorText,
  Kicker,
  Muted,
  Segmented,
  Skeleton,
  Stepper,
  Tag,
} from '@/shared/ui';
import styles from './blind.module.scss';

/**
 * Écran 05 — pronostic à l'aveugle (US-14).
 *
 * L'écran ne sait rien du rhum : l'API lui a envoyé « Rhum mystère n°2 » avec tous les
 * champs identifiants à null. Il n'y a donc rien à cacher côté client, et rien qu'un
 * inspecteur réseau pourrait révéler.
 */
export function BlindScreen(): ReactNode {
  const { id: sessionId = '' } = useParams();
  const navigate = useNavigate();
  const api = useApi();
  const { queueGuess } = useApp();

  const session = useAsync(() => api.sessions.byId(sessionId), [sessionId]);
  const blindSlot = session.data?.rums.find((slot) => slot.isBlind && !slot.revealedAt);
  const rumId = blindSlot?.rum.id ?? null;

  const [country, setCountry] = useState<string | null>(null);
  const [rumType, setRumType] = useState<RumType | null>(null);
  const [abv, setAbv] = useState<number | null>(null);
  const [age, setAge] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const progress = useAsync(
    () => (rumId ? api.guesses.progress(sessionId, rumId) : Promise.resolve(null)),
    [sessionId, rumId],
  );
  const own = useAsync(
    () => (rumId ? api.guesses.mine(sessionId, rumId) : Promise.resolve(null)),
    [sessionId, rumId],
  );

  // Reprise d'un pronostic déjà déposé.
  useEffect(() => {
    if (!own.data) return;
    setCountry(own.data.country);
    setRumType(own.data.rumType);
    setAbv(own.data.abvGuess);
    setAge(own.data.ageGuess);
  }, [own.data]);

  // La révélation bascule tous les écrans en même temps (US-15).
  useSessionSocket(sessionId, {
    onRumRevealed: () => void navigate(`/seances/${sessionId}/revelation`),
    onProgressChanged: progress.reload,
    onTastingOpened: session.reload,
  });

  if (session.loading) return <Skeleton />;
  if (session.error) return <EmptyState>{session.error}</EmptyState>;
  if (!blindSlot || !rumId) {
    return <EmptyState>Aucun rhum à l’aveugle en attente sur cette séance.</EmptyState>;
  }
  // Le pronostic suit la note : il ne s'ouvre qu'une fois la séance lancée. L'API le refuse
  // de toute façon, et l'écran se rouvrira seul à l'ouverture grâce au socket.
  if (session.data && session.data.status !== 'OPEN') {
    return (
      <EmptyState>
        {session.data.status === 'CLOSED'
          ? 'La séance est clôturée : les pronostics sont figés.'
          : 'La séance n’est pas encore lancée. Les pronostics ouvriront dès que la régie lancera la séance.'}
      </EmptyState>
    );
  }

  const locked = own.data?.lockedAt != null;

  async function lockIn(): Promise<void> {
    const parsed = upsertGuessSchema.safeParse({
      sessionId,
      rumId,
      country,
      rumType,
      abvGuess: abv,
      ageGuess: age,
      lock: true,
      clientUpdatedAt: new Date().toISOString(),
    });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Pronostic invalide.');
      return;
    }

    setBusy(true);
    setError(null);
    try {
      await queueGuess(parsed.data);
      own.reload();
      progress.reload();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <Kicker>Dégustation à l’aveugle</Kicker>
        <h1>
          <EyeOff size={22} strokeWidth={2.75} aria-hidden="true" /> {blindSlot.rum.name}
        </h1>
        <Muted>
          Fiche masquée par l’administrateur
          {blindSlot.rum.broughtBy ? ` · apporté par ${blindSlot.rum.broughtBy.pseudo}` : ''}
        </Muted>
      </header>

      <section className={styles.guess}>
        <header className={styles.sectionHead}>
          <h2>Votre pronostic</h2>
          <Muted>Verrouillé à la validation, invisible des autres avant la révélation.</Muted>
        </header>

        <fieldset className={styles.group} disabled={locked}>
          <legend>Pays</legend>
          <div className={styles.chips}>
            {COUNTRY_SUGGESTIONS.slice(0, 7).map((option) => (
              <Tag
                key={option}
                selected={country === option}
                onClick={() => setCountry(country === option ? null : option)}
              >
                {option}
              </Tag>
            ))}
          </div>
        </fieldset>

        <fieldset className={styles.group} disabled={locked}>
          <legend>Type</legend>
          <Segmented
            label="Type de rhum"
            value={rumType}
            onChange={setRumType}
            options={RUM_TYPES.filter((t) => t !== 'AUTRE').map((value) => ({
              value,
              label: RUM_TYPE_LABELS[value],
            }))}
          />
        </fieldset>

        <fieldset className={styles.group} disabled={locked}>
          <legend>Degré</legend>
          <Stepper
            label="degré"
            value={abv}
            display={abv === null ? '—' : `${String(abv).replace('.', ',')} %`}
            step={0.5}
            min={30}
            max={80}
            onChange={setAbv}
          />
        </fieldset>

        <fieldset className={styles.group} disabled={locked}>
          <legend>Âge</legend>
          <Stepper
            label="âge"
            value={age}
            display={age === null ? '—' : `${age} ans`}
            step={1}
            min={0}
            max={50}
            onChange={setAge}
          />
        </fieldset>
      </section>

      <footer className={styles.actions}>
        {locked && progress.data ? (
          <>
            <div className={styles.waiting}>
              <span className={styles.waitingHalo} aria-hidden="true" />
              <span className={styles.waitingPill}>
                <span className={styles.waitingDot} />
                <strong>En attente de la révélation…</strong>
              </span>
            </div>
            <p className={styles.waitingProgress}>
              {progress.data.lockedGuesses} membre
              {progress.data.lockedGuesses > 1 ? 's' : ''} sur {progress.data.expectedGuesses} ont
              validé
            </p>
          </>
        ) : null}

        <ErrorText>{error}</ErrorText>

        {locked ? (
          <p className={styles.lockedNotice}>
            <Lock size={14} strokeWidth={2.75} aria-hidden="true" /> Pronostic verrouillé.
          </p>
        ) : (
          <Button block disabled={busy} onClick={() => void lockIn()}>
            Verrouiller mon pronostic
          </Button>
        )}
      </footer>
    </div>
  );
}
